import { motion } from "framer-motion";
import { FaCheckCircle, FaUpload, FaRocket, FaChartLine } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Images from "../../utils/Images";

export default function InsuranceChoices() {
  const navigate = useNavigate();

  const handleStartJourney = () => {
    navigate('/signup');
  };

  const steps = [
    {
      id: 1,
      number: "1",
      title: "Create Provider Account",
      description: "Submit your company info & regulatory documents.",
      icon: FaCheckCircle,
      isActive: true
    },
    {
      id: 2,
      number: "2",
      title: "Upload Policies",
      description: "Define policy types, coverage levels, and pricing.",
      icon: FaUpload,
      isActive: false
    },
    {
      id: 3,
      number: "3",
      title: "Go Live",
      description: "Get listed on Trip Secure for travelers to discover.",
      icon: FaRocket,
      isActive: false
    },
    {
      id: 4,
      number: "4",
      title: "Track & Earn",
      description: "Access real-time data on sales & claims.",
      icon: FaChartLine,
      isActive: false
    }
  ];

  return (
    <section className="overflow-x-hidden px-4 py-20 bg-green-50 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
          
          {/* Left Column - Illustration */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative flex justify-center lg:justify-start"
          >
            <div className="absolute inset-10 rounded-full bg-emerald-300/30 blur-3xl pointer-events-none" />
            <img
              src={Images.how_it_works_img}
              alt="Four steps to go live on TripSecure"
              className="relative z-10 w-full max-w-md drop-shadow-xl select-none sm:max-w-lg lg:max-w-none"
            />
          </motion.div>

          {/* Right Column - Steps */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left"
            >
              <h3 className="mb-2 text-lg font-medium text-gray-700">
                How Trip Secure Works
              </h3>
              <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl lg:text-5xl">
                Four Steps and Done
              </h2>
            </motion.div>

            {/* Steps */}
            <div className="space-y-6">
              {steps.map((step, index) => (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative"
                >
                  {/* Step Content */}
                  <div className="flex items-start space-x-4">
                    {/* Step Number */}
                    <div className="relative flex-shrink-0">
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                          step.isActive
                            ? "bg-black text-white shadow-lg"
                            : "bg-white text-gray-700 border-2 border-gray-700"
                        }`}
                      >
                        {step.number}
                      </motion.div>
                      
                      {/* Active Indicator */}
                      {step.isActive && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.3, delay: 0.2 }}
                          className="flex absolute -top-1 -right-1 justify-center items-center w-4 h-4 bg-black rounded-full"
                        >
                          <div className="w-2 h-2 bg-white rounded-full" />
                        </motion.div>
                      )}
                    </div>

                    {/* Step Info */}
                    <div className="flex-1 pt-1">
                      <h3 className="mb-2 text-xl font-semibold text-gray-900">
                        {step.title}
                      </h3>
                      <p className="leading-relaxed text-gray-600">
                        {step.description}
                      </p>
                    </div>

                    {/* Step Icon */}
                    <div className="flex-shrink-0 pt-1">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        step.isActive ? "bg-blue-100" : "bg-gray-100"
                      }`}>
                        <step.icon 
                          size={16} 
                          className={step.isActive ? "text-blue-600" : "text-gray-500"} 
                        />
                      </div>
                    </div>
                  </div>

                  {/* Animated Connector */}
                  {index < steps.length - 1 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      whileInView={{ height: "40px", opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: (index + 1) * 0.1 }}
                      className="absolute left-6 top-12 w-0.5 bg-gradient-to-b from-gray-300 to-gray-200"
                    >
                      {/* Animated dots moving down the connector */}
                      <motion.div
                        animate={{ 
                          y: [0, 40],
                          opacity: [1, 0]
                        }}
                        transition={{ 
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: index * 0.5
                        }}
                        className="absolute -left-1 w-2 h-2 bg-blue-500 rounded-full"
                      />
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="pt-6 text-center lg:text-left"
            >
              <div className="inline-flex items-center px-6 py-3 mb-4 text-blue-700 bg-blue-50 rounded-full">
                <FaCheckCircle size={20} className="mr-2" />
                <span className="font-medium">Ready to get started?</span>
              </div>
              <div>
                <button 
                  onClick={handleStartJourney}
                  className="px-8 py-3 font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-700 rounded-full shadow-lg transition-all duration-300 hover:from-blue-700 hover:to-indigo-800 hover:scale-105"
                >
                  Start Your Journey
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
